import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendUp, Buildings, Plus, SignOut, CaretRight, Globe, Users, X
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import Logo from '@/components/Logo';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompaniesPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '', industry: '', country: '', business_model: '', website: '', employee_count: '', annual_revenue: ''
  });
  const [assessmentData, setAssessmentData] = useState({ assessment_type: 'ecommerce', level: 'free' });

  const industries = [
    { key: 'retail', label: t('industry.retail') },
    { key: 'fashion', label: t('industry.fashion') },
    { key: 'electronics', label: t('industry.electronics') },
    { key: 'food', label: t('industry.food') },
    { key: 'health', label: t('industry.health') },
    { key: 'home', label: t('industry.home') },
    { key: 'sports', label: t('industry.sports') },
    { key: 'automotive', label: t('industry.automotive') },
    { key: 'b2b', label: t('industry.b2b') },
    { key: 'other', label: t('industry.other') },
  ];

  const countriesList = [
    { key: 'us', label: t('country.us') },
    { key: 'uk', label: t('country.uk') },
    { key: 'de', label: t('country.de') },
    { key: 'fr', label: t('country.fr') },
    { key: 'tr', label: t('country.tr') },
    { key: 'nl', label: t('country.nl') },
    { key: 'es', label: t('country.es') },
    { key: 'it', label: t('country.it') },
    { key: 'ca', label: t('country.ca') },
    { key: 'au', label: t('country.au') },
    { key: 'other', label: t('country.other') },
  ];

  const businessModels = [
    { key: 'b2c', label: t('model.b2c') },
    { key: 'b2b', label: t('model.b2b') },
    { key: 'b2b2c', label: t('model.b2b2c') },
    { key: 'd2c', label: t('model.d2c') },
    { key: 'marketplace', label: t('model.marketplace') },
  ];

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() });
      setCompanies(response.data);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally { setLoading(false); }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/companies`, formData, { headers: getAuthHeaders() });
      setCompanies([...companies, response.data]);
      setShowModal(false);
      setFormData({ name: '', industry: '', country: '', business_model: '', website: '', employee_count: '', annual_revenue: '' });
      toast.success(t('companies.create') + '!');
    } catch (error) {
      toast.error('Failed to create company');
    }
  };

  const handleStartAssessment = async () => {
    if (!selectedCompany) return;
    try {
      const response = await axios.post(`${API_URL}/assessments`, {
        company_id: selectedCompany.id, ...assessmentData
      }, { headers: getAuthHeaders() });
      navigate(`/assessment/${selectedCompany.id}/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to start assessment');
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-[#52525B] hover:text-[#09090B] transition-colors">{t('nav.dashboard')}</Link>
              <Link to="/companies" className="text-[#09090B] font-medium">{t('nav.companies')}</Link>
              <Link to="/advisor" className="text-[#52525B] hover:text-[#09090B] transition-colors">{t('nav.advisor')}</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#0F1B36] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout} className="text-[#E60000]">
                  <SignOut size={16} className="mr-2" />{t('nav.signout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-2 text-[#09090B] mb-2" data-testid="companies-title">{t('companies.title')}</h1>
            <p className="text-[#52525B]">{t('companies.subtitle')}</p>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none" data-testid="add-company-btn">
            <Plus size={20} className="mr-2" />{t('companies.add')}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#52525B]">{t('common.loading')}</div>
        ) : companies.length === 0 ? (
          <div className="bg-white border border-[#E4E4E7] p-12 text-center">
            <Buildings size={64} className="text-[#E4E4E7] mx-auto mb-4" />
            <h2 className="heading-4 text-[#09090B] mb-2">{t('companies.no_companies')}</h2>
            <p className="text-[#52525B] mb-6">{t('companies.no_companies_desc')}</p>
            <Button onClick={() => setShowModal(true)} className="bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none" data-testid="cta-add-company">
              <Plus size={20} className="mr-2" />{t('companies.add_first')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company) => (
              <div key={company.id} className="bg-white border border-[#E4E4E7] p-6 hover:-translate-y-1 hover:shadow-[0_4px_0_0_#0F1B36] transition-all duration-200" data-testid={`company-card-${company.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#F4F4F5] flex items-center justify-center">
                    <Buildings size={24} className="text-[#0F1B36]" />
                  </div>
                </div>
                <h3 className="heading-4 text-[#09090B] mb-2">{company.name}</h3>
                <div className="space-y-2 mb-6">
                  {company.industry && (
                    <div className="flex items-center gap-2 text-sm text-[#52525B]"><Users size={16} /><span>{company.industry}</span></div>
                  )}
                  {company.country && (
                    <div className="flex items-center gap-2 text-sm text-[#52525B]"><Globe size={16} /><span>{company.country}</span></div>
                  )}
                </div>
                <Button onClick={() => { setSelectedCompany(company); setShowAssessmentModal(true); }}
                  className="w-full bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none" data-testid={`start-assessment-${company.id}`}
                >
                  {t('companies.start_assessment')}<CaretRight size={16} className="ml-2" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg rounded-none border border-[#E4E4E7]">
          <DialogHeader>
            <DialogTitle className="heading-4 text-[#09090B]">{t('companies.dialog.title')}</DialogTitle>
            <DialogDescription className="text-[#52525B]">{t('companies.dialog.subtitle')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('companies.field.name')} *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="rounded-none" required data-testid="company-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('companies.field.industry')}</Label>
                <Select value={formData.industry} onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                  <SelectTrigger className="rounded-none" data-testid="company-industry-select"><SelectValue placeholder={t('companies.field.select')} /></SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (<SelectItem key={ind.key} value={ind.label}>{ind.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('companies.field.country')}</Label>
                <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                  <SelectTrigger className="rounded-none" data-testid="company-country-select"><SelectValue placeholder={t('companies.field.select')} /></SelectTrigger>
                  <SelectContent>
                    {countriesList.map((c) => (<SelectItem key={c.key} value={c.label}>{c.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('companies.field.model')}</Label>
              <Select value={formData.business_model} onValueChange={(value) => setFormData({ ...formData, business_model: value })}>
                <SelectTrigger className="rounded-none" data-testid="company-model-select"><SelectValue placeholder={t('companies.field.select')} /></SelectTrigger>
                <SelectContent>
                  {businessModels.map((bm) => (<SelectItem key={bm.key} value={bm.label}>{bm.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('companies.field.website')}</Label>
              <Input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://example.com" className="rounded-none" data-testid="company-website-input" />
            </div>
            <div className="flex justify-end gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">{t('common.cancel')}</Button>
              <Button type="submit" className="bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none" data-testid="company-submit-btn">{t('companies.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssessmentModal} onOpenChange={setShowAssessmentModal}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#E4E4E7]">
          <DialogHeader>
            <DialogTitle className="heading-4 text-[#09090B]">{t('companies.assessment.dialog.title')}</DialogTitle>
            <DialogDescription className="text-[#52525B]">{t('companies.assessment.dialog.subtitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t('companies.assessment.type')}</Label>
              <Select value={assessmentData.assessment_type} onValueChange={(value) => setAssessmentData({ ...assessmentData, assessment_type: value })}>
                <SelectTrigger className="rounded-none" data-testid="assessment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ecommerce">{t('assessment.type.ecommerce')}</SelectItem>
                  <SelectItem value="eexport">{t('assessment.type.eexport')}</SelectItem>
                  <SelectItem value="both">{t('assessment.type.ecommerce')} + {t('assessment.type.eexport')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('companies.assessment.level')}</Label>
              <Select value={assessmentData.level} onValueChange={(value) => setAssessmentData({ ...assessmentData, level: value })}>
                <SelectTrigger className="rounded-none" data-testid="assessment-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">{t('assessment.level.free')}</SelectItem>
                  <SelectItem value="basic">{t('assessment.level.basic')}</SelectItem>
                  <SelectItem value="advanced">{t('assessment.level.advanced')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-4 pt-4">
              <Button variant="outline" onClick={() => setShowAssessmentModal(false)} className="rounded-none">{t('common.cancel')}</Button>
              <Button onClick={handleStartAssessment} className="bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none" data-testid="start-assessment-btn">
                {t('companies.assessment.start')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesPage;
